import * as turf from "@turf/turf";
import type { FormikProps } from "formik";
import type { Feature, Geometry } from "geojson";
import moment from "moment/min/moment-with-locales";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthContext,
  useConvertCase,
  useGlobalState,
} from "senf-shared/index";
import type { Project } from "senf-shared/types";
import { getZoomLevel } from "../../atoms/map/utils/helpers";
import { useOnDragEnd } from "../../atoms/sortableList/Sortable";
import type { IconName } from "../../core/icon/names";
import type {
  GeoDataObject,
  QuestionEntry,
  QuestionValue,
  SurveyEntry,
  SurveyEntryResults,
} from "./results.config";
import {
  getTextResponseSetting,
  type InputColumn,
  type InputRow,
  isMapQuestion,
  isRatingQuestion,
  isStringyQuestion,
  type Page,
  type PageType,
  pageTypeConfig,
  type QuestionType,
} from "./survey.config";
import { getMostRecentlyViewedPageIndex, type useEditQuestion } from "./utils";

export const getMapType = (page?: Page<PageType>) => {
  if (!page) return undefined;
  const typeMap: Partial<Record<PageType, string>> = {
    mapPoint: "point",
    mapPath: "path",
    mapArea: "area",
  };
  return page.type in typeMap ? typeMap[page.type] : page.type;
};

type SurveyNavigationProps = {
  pages: Page[];
  index: number;
  formik?: FormikProps<SurveyEntryResults>;
  preview?: boolean;
};

type PrepareSurveyPageProps = SurveyNavigationProps & {
  isMobile?: boolean;
  project?: Project;
};

export type ChartViews =
  | "grid"
  | "heatmap"
  | "drawn"
  | "hexabin"
  | "pie"
  | "bar"
  | "horizontalBar"
  | "line"
  | "wordCloud"
  | "scatter"
  | "table";
// | "list"

type ChartViewBaseProps = {
  icon: IconName;
  type: "map" | "chart" | "wordCloud" | "table";
};
export type ChartViewConfig = Record<
  ChartViews,
  ChartViewBaseProps & {
    text: string;
    active: boolean;
  }
>;

export const gridTypeProps = {
  heatmap: {
    icon: "map-heat",
    type: "map",
  },
  hexabin: {
    icon: "map-hexabin",
    type: "map",
  },
  drawn: {
    icon: "map-drawn",
    type: "map",
  },
  grid: {
    icon: "statistics-grid",
    type: "chart",
  },
  pie: {
    icon: "statistics-pie",
    type: "chart",
  },
  horizontalBar: {
    icon: "statistics-bar",
    type: "chart",
  },
  bar: {
    icon: "statistics-column",
    type: "chart",
  },
  line: {
    icon: "statistics-line",
    type: "chart",
  },
  scatter: {
    icon: "statistics-scatter",
    type: "chart",
  },
  wordCloud: {
    icon: "wordcloud",
    type: "wordCloud",
  },
  table: {
    icon: "table",
    type: "table",
  },
  // list: {
  //   icon: "list",
  //   type: "table",
  // },
} satisfies Record<ChartViews, ChartViewBaseProps>;

export type GridTypeProps = {
  [K in keyof typeof gridTypeProps]: (typeof gridTypeProps)[K] & { name: K };
}[keyof typeof gridTypeProps];

const storedRandomOrder: Record<string, InputRow<PageType>[]> = {};

const generate = (arr: InputRow<PageType>[]) =>
  Array.from(arr, () => Math.floor(Math.random() * 100));

const isAscending = (arr: number[]) =>
  arr.every(
    (value, index, array) =>
      index === array.length - 1 || value < array[index + 1],
  );

const randomize = (arr: InputRow<PageType>[]): InputRow<PageType>[] => {
  let order = [] as number[];
  while (isAscending(order)) order = generate(arr);

  return arr
    .map((e, i) => ({ item: e, order: order[i] }))
    .sort((a, b) => a.order - b.order)
    .map(({ item }) => item);
};

export const usePrepareSurveyPage = ({
  pages,
  index,
  isMobile,
  project,
}: PrepareSurveyPageProps) => {
  const { t } = useTranslation();
  const { toTitleCase } = useConvertCase();
  const getPageByIndex = (i = index) => {
    const currentPage = pages?.[i] as Page;
    if (!currentPage) return undefined;

    const currentPageMapData =
      currentPage?.map?.mapId && currentPage?.map?.mapId !== ""
        ? project?.maps?.find((map) => map.mapId === currentPage?.map?.mapId)
        : null;
    // const { mapDataLoading, error, mapData } = useMapData(
    //   project,
    //   currentPage?.mapId
    // );

    const outroIndex = (pages?.length || 0) - 1;

    const pageRoles = ["intro", "outro", "demographics"];

    const currentPageRole = pageRoles.includes(currentPage?.type)
      ? currentPage?.type
      : "surveyPage";

    const currentPageIsMapbased = isMapQuestion(currentPage);

    const currentPageHasMap = !!(
      currentPageIsMapbased ||
      (currentPage?.map?.mapId && currentPage?.map?.mapId !== "")
    );

    if (
      (currentPageIsMapbased || currentPageHasMap) &&
      currentPageMapData?.mapbounds
    ) {
      const [longitude, latitude] = turf.center(currentPageMapData?.mapbounds)
        ?.geometry?.coordinates || [51.1657, 10.4515];
      const zoom = getZoomLevel(turf.bbox(currentPageMapData?.mapbounds));

      currentPageMapData.initialMapViewport = {
        latitude,
        longitude,
        zoom,
        duration: 0,
        pitch: 0,
        bearing: 50,
        maxZoom: 20,
        minZoom: 5,
      };
    }

    const currentPageHasQuestion =
      !pageTypeConfig[currentPage?.type]?.structuralPage;

    const currentPageImages = currentPage?.images?.items;
    const currentPageHasImage = currentPageImages?.length;

    const currentPageAlignment =
      (currentPage?.type === "intro" || currentPage?.type === "outro") &&
      ((!currentPageHasImage && !currentPageHasMap) || isMobile)
        ? ("center" as const)
        : undefined;

    const progress = Math.max(0, Math.min(i / outroIndex, 1) * 100);

    return {
      currentPage,
      currentPageRole,
      currentPageIsMapbased,
      currentPageHasMap,
      currentPageHasImage,
      currentPageHasQuestion,
      currentPageImages,
      currentPageAlignment,
      currentPageMapData,
      progress,
    };
  };

  const getViewsConfig = (currentPageIndex: number) => {
    const { currentPageIsMapbased, currentPage, currentPageRole } =
      getPageByIndex(currentPageIndex) || {};
    const isSurveyPage = currentPageRole === "surveyPage";
    if (!currentPage) return {} as ChartViewConfig;

    const mapQuestion = isMapQuestion(currentPage);
    const isMapOrImageAnnotation = currentPageIsMapbased;
    const isContactInputPage = currentPage?.type === "contactInput";

    const hasBooleanResponse = !!(
      currentPage?.type === "choice" ||
      currentPage?.type === "choiceImage" ||
      currentPage?.type === "choiceMatrix" ||
      currentPage?.type === "budgetAllocation" ||
      currentPage?.type === "ratingScale" ||
      currentPage?.type === "ratingSlider" ||
      currentPage?.type === "ratingStars" ||
      currentPage?.type === "ranking" ||
      (mapQuestion && currentPage?.settings?.valueType === "boolean")
    );

    const hasNumberResponse =
      currentPage?.type === "numberInput" ||
      (mapQuestion && currentPage?.settings?.valueType === "number") ||
      isRatingQuestion(currentPage);

    // Contact answers are PII and not meant for analytical chart views
    // (pie/bar/word cloud), so we exclude them from "stringy" here even though
    // contactInput is technically a string-valued question type.
    const hasStringyResponse =
      !isContactInputPage && isStringyQuestion(currentPage);

    return {
      heatmap: {
        ...gridTypeProps.heatmap,
        text: toTitleCase(t("global:heatmap")),
        active: isMapOrImageAnnotation,
      },
      drawn: {
        ...gridTypeProps.drawn,
        text: toTitleCase(t("global:map")),
        active: isMapOrImageAnnotation,
      },
      hexabin: {
        ...gridTypeProps.hexabin,
        text: toTitleCase(t("global:hexabin")),
        active: isMapOrImageAnnotation,
      },
      grid: {
        ...gridTypeProps.grid,
        text: toTitleCase(t("global:gridChart")),
        active:
          isSurveyPage &&
          !isMapOrImageAnnotation &&
          currentPage?.type !== "textResponse" &&
          !isContactInputPage,
      },
      pie: {
        ...gridTypeProps.pie,
        text: toTitleCase(t("global:pieChart")),
        active:
          isSurveyPage &&
          (hasStringyResponse || hasBooleanResponse || hasNumberResponse),
      },
      bar: {
        ...gridTypeProps.bar,
        text: toTitleCase(t("global:columnChart")),
        active:
          isSurveyPage &&
          (hasStringyResponse || hasBooleanResponse || hasNumberResponse),
      },
      horizontalBar: {
        ...gridTypeProps.horizontalBar,
        text: toTitleCase(t("global:barChart")),
        active:
          isSurveyPage &&
          (hasStringyResponse || hasBooleanResponse || hasNumberResponse),
      },
      line: {
        ...gridTypeProps.line,
        text: toTitleCase(t("global:lineChart")),
        active:
          isSurveyPage &&
          (hasStringyResponse || hasBooleanResponse || hasNumberResponse),
      },
      scatter: {
        ...gridTypeProps.scatter,
        text: toTitleCase(t("global:scatterChart")),
        active: isSurveyPage && currentPage?.type === "budgetAllocation",
      },
      wordCloud: {
        ...gridTypeProps.wordCloud,
        text: toTitleCase(t("global:wordCloud")),
        active: hasStringyResponse,
      },
      table: {
        ...gridTypeProps.table,
        text: toTitleCase(t("global:table")),
        active: isContactInputPage || isStringyQuestion(currentPage, true), // only show table as option for pure text response / contact questions,
      },
    } as ChartViewConfig;
  };

  return {
    ...getPageByIndex(),
    getPageByIndex,
    getViewsConfig,
  };
};

const getCondition = ({ pages, index, formik }: SurveyNavigationProps) => {
  const page = pages[index];
  const { values } = (page && formik?.values[page.uid]) || {};
  const matchingCondition = page?.conditions?.find(({ type, response }) => {
    if (type === "equals") {
      return values?.some((e) => e.columnId === response);
    }
    if (type === "notEquals") {
      return !values?.some((e) => e.columnId === response);
    }
    return false;
  });
  if (matchingCondition) return matchingCondition;
  const noneCondition = page?.conditions?.find((c) => c.type === "none");
  if (noneCondition) return noneCondition;
  return undefined;
};

export const getNextIndex = ({
  pages,
  index,
  formik,
}: SurveyNavigationProps) => {
  const condition = getCondition({ pages, index, formik });
  const incrementByOne =
    pages[index]?.type === "intro" ||
    !condition ||
    condition.goToPage === "next";
  if (incrementByOne) return index + 1;
  const targetIndex = pages.findIndex((e) => e.uid === condition.goToPage);
  return targetIndex >= 0 ? targetIndex : index + 1;
};

const useProfileCompletedInOtherSession = () => {
  const { user } = useAuthContext();
  const key = useMemo(() => `${user?.userId}-profileCompleted`, [user?.userId]);
  const isProfileCompleted = () =>
    user && sessionStorage.getItem(key) === "true";
  useEffect(() => {
    if (user && sessionStorage.getItem(key) == null)
      sessionStorage.setItem(key, user.profileCompleted?.toString() || "false");
  }, [user, key]);
  return isProfileCompleted;
};

export const useNavigationIndex = ({
  pages,
  index,
  formik,
  preview,
}: SurveyNavigationProps) => {
  const isProfileCompleted = useProfileCompletedInOtherSession();

  const profileCompleted = isProfileCompleted() && !preview;

  const { previousIndex, nextIndex } = useMemo(() => {
    let previousIndex = getMostRecentlyViewedPageIndex(
      pages,
      formik?.values,
      index,
      1,
    );
    // this is a special case when demographics is the first page and it is completed, then there is no timestamp for this page
    if (pages[previousIndex]?.type === "demographics" && profileCompleted) {
      previousIndex--;
    }
    let nextIndex = getNextIndex({ pages, index, formik });
    if (pages[nextIndex]?.type === "demographics" && profileCompleted) {
      nextIndex = getNextIndex({ pages, index: nextIndex, formik });
    }
    return {
      previousIndex,
      nextIndex,
    };
  }, [pages, index, formik, profileCompleted]);

  return {
    nextIndex,
    previousIndex,
    isLastPage: pages[nextIndex]?.type === "outro",
  };
};

/**
 * if you need to access the defaults in a loop, don't pass
 * the page to the hook. The hook will return a function
 * which can be used in the loop.
 * */
type ReturnedDefaults = {
  defaultTitle: string;
  placeholderTitle: string;
  placeholderTextResponse: string;
  defaultSubtitle: string;
  defaultOtherLabel: string;
  defaultSelectLabel: string;
};
export function useDefaultValues(): (p: Page | undefined) => ReturnedDefaults;
export function useDefaultValues(p: Page | undefined): ReturnedDefaults;
export function useDefaultValues(p?: Page | undefined) {
  const { t } = useTranslation();

  const getDefaults = useMemo(
    () => (page: Page) => {
      const defaultTitleMap: Partial<Record<PageType, string>> = {
        intro: "survey:builder.titleOfIntro",
        outro: "survey:builder.titleOfOutro",
        section: "survey:builder.titleOfYourSection",
        demographics: "global:demographicData",
      };
      const defaultTitle = defaultTitleMap[page?.type]
        ? t(defaultTitleMap[page?.type] || "")
        : undefined;

      const placeholderTitle =
        defaultTitle || t("survey:builder.titleOfYourQuestion");

      const getFeatureType = (type: string, count: number) => {
        return t(count === 1 ? `global:${type}` : `global:${type}s`);
      };

      const getChoiceType = (count: number) => {
        return t(count === 1 ? "global:option" : "global:options");
      };

      const isChoicePage =
        page?.type === "choice" ||
        page?.type === "choiceImage" ||
        page?.type === "choiceMatrix";

      const isMapPage =
        page?.type === "mapPoint" ||
        page?.type === "mapPath" ||
        page?.type === "mapArea";

      const minMaxChoiceKey = isChoicePage
        ? !page?.settings.multipleChoice?.enabled
          ? "youCanOnlySelectOneOption"
          : page?.settings.multipleChoice.min &&
              page?.settings.multipleChoice.max &&
              page?.settings.multipleChoice.min ===
                page?.settings.multipleChoice.max
            ? "youMustSelectExactlyChoices"
            : page?.settings.multipleChoice.min &&
                page?.settings.multipleChoice.max
              ? "youMustSelectMinMaxChoices"
              : !page?.settings.multipleChoice.min &&
                  page?.settings.multipleChoice.max
                ? "youMustSelectMaxChoices"
                : page?.settings.multipleChoice.min &&
                    !page?.settings.multipleChoice.max
                  ? "youMustSelectMinChoices"
                  : "youCanSelectMultipleOptions"
        : "";

      const minMaxFeatureKey = isMapPage
        ? !page?.settings.mapResponse?.limit?.enabled
          ? "youCanAddMultipleFeatures"
          : page?.settings.mapResponse?.limit?.min &&
              page?.settings.mapResponse?.limit?.max &&
              page?.settings.mapResponse?.limit?.min ===
                page?.settings.mapResponse?.limit?.max
            ? "youMustAddExactlyFeatures"
            : page?.settings.mapResponse?.limit?.min &&
                page?.settings.mapResponse?.limit?.max
              ? "youMustAddMinMaxFeatures"
              : !page?.settings.mapResponse?.limit?.min &&
                  page?.settings.mapResponse?.limit?.max
                ? "youMustAddMaxFeatures"
                : page?.settings.mapResponse?.limit?.min &&
                    !page?.settings.mapResponse?.limit?.max
                  ? "youMustAddMinFeatures"
                  : "youCanAddMultipleFeatures"
        : "";

      const choiceConfig = isChoicePage
        ? {
            count: !page?.settings?.multipleChoice?.enabled
              ? 1
              : page?.settings.multipleChoice.min ||
                page?.settings.multipleChoice.max ||
                2,
            maxCount: page?.settings.multipleChoice?.max,
            choiceType: getChoiceType(
              !page?.settings.multipleChoice?.enabled
                ? 1
                : page?.settings.multipleChoice.min ||
                    page?.settings.multipleChoice.max ||
                    2,
            ),
          }
        : undefined;
      const featureConfig = isMapPage
        ? {
            count: !page?.settings.mapResponse?.limit?.enabled
              ? 1
              : page?.settings.mapResponse?.limit.min ||
                page?.settings.mapResponse?.limit.max ||
                2,
            maxCount: page?.settings.mapResponse?.limit?.max,
            featureType: getFeatureType(
              getMapType(page) || "point",
              !page?.settings.mapResponse?.limit?.enabled
                ? 2
                : page?.settings.mapResponse?.limit.min ||
                    page?.settings.mapResponse?.limit.max ||
                    2,
            ),
          }
        : undefined;

      const defaultSubtitleMap: Partial<
        Record<
          PageType | "default",
          [string] | [string, Record<string, unknown> | undefined]
        >
      > = {
        choice: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        choiceImage: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        choiceMatrix: [`survey:options.${minMaxChoiceKey}`, choiceConfig],
        mapPoint: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        mapPath: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        mapArea: [`survey:options.${minMaxFeatureKey}`, featureConfig],
        ranking:
          page.type === "ranking"
            ? [
                `survey:options.${page?.settings?.limit?.enabled ? "rankingDescriptionWithLimit" : "rankingDescription"}`,
                {
                  count: page?.settings?.limit?.max || 1,
                },
              ]
            : undefined,
        default: ["survey:builder.titleOfYourQuestion"],
      };
      const [desc1, desc2] = defaultSubtitleMap[page?.type] ||
        defaultSubtitleMap?.default || [""];
      let defaultSubtitle = t(desc1, desc2);
      if (page?.type === "choiceMatrix") {
        defaultSubtitle += `, ${t("survey:options.perRow")}`;
      }

      const textPlaceholderMap: Partial<Record<PageType | "default", string>> =
        {
          choice: "survey:pleaseSpecify",
          ratingScale: "survey:elaborateBriefly",
          ratingStars: "survey:elaborateBriefly",
          ratingSlider: "survey:elaborateBriefly",
          mapPoint: t("util:enterDescriptionFor", {
            val: t(`global:${getMapType(page)}`),
          }),
          mapPath: t("util:enterDescriptionFor", {
            val: t(`global:${getMapType(page)}`),
          }),
          mapArea: t("util:enterDescriptionFor", {
            val: t(`global:${getMapType(page)}`),
          }),
          numberInput: "survey:pleaseEnter",
          contactInput: "global:contact",
          default: "survey:describeBriefly",
        };

      // We do this so that the mapbased radio other option gets the right placeholder
      const type =
        isMapPage && page?.settings?.valueType === "boolean"
          ? "choice"
          : page?.type;

      const placeholderTextResponse = t(
        textPlaceholderMap[type] || textPlaceholderMap?.default || "",
      );
      const defaultOtherLabel = t("global:other");
      const defaultSelectLabel = t("survey:selectOption");

      return {
        defaultTitle,
        placeholderTitle,
        defaultSubtitle,
        placeholderTextResponse,
        defaultOtherLabel,
        defaultSelectLabel,
      };
    },
    [
      t,
      p?.type,
      (p as Page<"choice">)?.settings?.multipleChoice?.enabled,
      (p as Page<"choice">)?.settings?.multipleChoice?.min,
      (p as Page<"choice">)?.settings?.multipleChoice?.max,
      (p as Page<"mapPoint">)?.settings?.valueType,
      (p as Page<"mapPoint">)?.settings?.mapResponse?.limit?.enabled,
      (p as Page<"mapPoint">)?.settings?.mapResponse?.limit?.min,
      (p as Page<"mapPoint">)?.settings?.mapResponse?.limit?.max,
      (p as Page<"ranking">)?.settings?.limit?.enabled,
    ],
  );

  return useMemo(() => (p ? getDefaults(p) : getDefaults), [getDefaults, p]);
}

const shuffleOptions = <
  T extends QuestionType,
  K extends "columns" | "rows" = "rows",
>({
  mode,
  page,
  key,
  remember,
}: {
  mode: "display" | "edit";
  page: Page<T>;
  key: "columns" | "rows";
  remember: boolean;
}) => {
  const currentOptions = page.options?.[key] || [];

  if (mode === "edit" || !page?.settings?.shuffle || !page.options?.[key]) {
    return currentOptions;
  }

  const storedOrder = storedRandomOrder[page.uid];
  const optionsChanged =
    !storedOrder || storedOrder.length !== currentOptions.length;

  // Generate new random order if:
  // - Not in memory
  // - Not remembering
  // - Options have changed
  if (!storedOrder || optionsChanged || !remember) {
    const newOrder = randomize(currentOptions);
    storedRandomOrder[page.uid] = newOrder;
    return newOrder;
  }

  return storedOrder as NonNullable<Page<T>["options"]>[K];
};

export const useRandomSorting = <
  T extends QuestionType,
  K extends "columns" | "rows" = "rows",
>(
  page: Page<T>,
  key: K,
  mode: "edit" | "display" = "display",
  remember = true,
): NonNullable<Page<T>["options"]>[K] => {
  type Cells = NonNullable<Page<T>["options"]>[K];
  const [sortedOptions, setSortedOptions] = useState<Cells>(
    shuffleOptions<T, K>({ mode, page, key, remember }),
  );

  // Create a string representation of the current options order for deep comparison
  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to compare the options order when the options change
  const optionsOrderKey = useMemo(() => {
    return (
      page.options?.[key]?.map((item: Cells[number]) => item.uid).join(",") ||
      ""
    );
  }, [page.options?.[key]]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to shuffle the options when the options change
  useEffect(() => {
    setSortedOptions(shuffleOptions<T, K>({ mode, page, key, remember }));
  }, [mode, page, key, remember, optionsOrderKey]);

  return sortedOptions;
};

interface UseVerifyFilesProps {
  mimeTypes?: string[];
  maxSize?: number;
}
export const useVerifyFiles = ({
  mimeTypes = ["image/", "video/"], // Added "video/" to support video files
  maxSize = 20000000, // 20 MB
}: UseVerifyFilesProps = {}) => {
  const [invalidType, setInvalidType] = useState(false);
  const [invalidSize, setInvalidSize] = useState(false);

  const validType = (type: string) =>
    mimeTypes.some((mimeType) => type.startsWith(mimeType));

  const validSize = (size: number) => size < maxSize;

  const checkFileUpload = ({ files }) => {
    const validFiles = [] as File[];

    [...(files || [])].forEach((file) => {
      if (validType(file.type)) {
        if (validSize(file.size)) validFiles.push(file);
        else setInvalidSize(true);
      } else setInvalidType(true);
    });

    setTimeout(
      () => {
        setInvalidSize(false);
        setInvalidType(false);
      },
      validFiles.length ? 0 : 2000,
    );

    return validFiles;
  };

  return { invalidType, invalidSize, checkFileUpload };
};

export const useOverflow = (ref?: React.RefObject<HTMLDivElement | null>) => {
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const { scrollWidth, clientWidth } = ref?.current || {
    scrollWidth: 0,
    clientWidth: 0,
  };
  useEffect(() => {
    const r = ref?.current;
    const handleScroll = () => setScrollLeft(r?.scrollLeft || 0);
    r?.addEventListener("scroll", handleScroll);
    return () => r?.removeEventListener("scroll", handleScroll);
  }, [ref?.current]);

  const direction = document.documentElement.getAttribute("dir");
  const start =
    scrollWidth > clientWidth &&
    (direction === "ltr" ? scrollLeft > 5 : scrollLeft < scrollWidth - 5);
  const end =
    scrollWidth > clientWidth &&
    (direction === "ltr"
      ? scrollLeft + clientWidth < scrollWidth - 5
      : scrollLeft < 5);

  return {
    start,
    end,
  };
};

export const useSurveyImageSettings = () => {
  return {
    rowGrid: 5,
    defaultImageDimensions: {
      w: 2,
      h: 5,
      x: 0,
      y: 0,
    },
  };
};

export const useFormatDuration = () => {
  const formatDuration = (duration: number) => {
    if (!duration) return "N/A";
    if (duration < 1000) return "< 1s";
    return moment
      .utc(moment.duration(duration).asMilliseconds())
      .format(duration >= 3600000 ? "H[h] m[m] s[s]" : "m[m] s[s]");
  };
  return { formatDuration };
};

const getValuesForKey = (entries: SurveyEntry[], pageId: string) => {
  return entries
    .map((obj) => {
      const questionEntry = obj.results?.[pageId] as QuestionEntry<PageType>;
      if (questionEntry) {
        return {
          values: questionEntry.values?.filter(
            ({ value } = {}) =>
              value !== undefined && value !== null && value !== "",
          ),
          features: obj.geoData?.features?.filter(
            (feature) => feature.properties?.pageId === pageId,
          ),
        };
      }
      return {};
    })
    .filter((entry) => {
      return entry.values?.length || entry.features?.length;
    })
    .map((obj) => obj.values || []);
};

export const useGenerateStats = ({
  results,
  pages,
}: {
  results: SurveyEntry[];
  pages: Page[];
}) => {
  const { i18n } = useTranslation();

  const pagesById = pages.reduce(
    (acc, page) => {
      acc[page.uid] = page;
      return acc;
    },
    {} as Record<string, Page>,
  );

  const completeDemographics = (result: SurveyEntry) => {
    const { age, gender } = result.userData || {};
    const postcode = result.userData?.postcode;
    return String(age)?.trim() || gender?.trim() || postcode?.trim();
  };

  /**
   * Returns the results that were answered for a specific question
   * @returns
   */
  const answeredQuestion = (
    { type, uid }: Page<PageType>,
    entries = results || [],
  ) => {
    const values =
      type === "section"
        ? entries.filter((entry) => {
            // For section pages, count only entries that saw the page (have createdAt)
            const pageResult = entry.results?.[uid];
            return !!pageResult?.timeStamp;
          })
        : type === "intro" || type === "outro"
          ? entries
          : type === "demographics"
            ? entries.filter(completeDemographics)
            : getValuesForKey(entries, uid);
    return values;
  };

  // const hasValue = (values: SurveyEntry, question: Page) => {
  //   if (question.type === "demographics") return completeDemographics(values);

  //   return getValuesForKey(results, question.uid).length
  //   const r = values.values[question.uid];
  //   console.log(question?.title, r);
  //   return r?.value || r?.geoData || r?.comment;
  // };
  // const legacyBounceRate = true;
  // @todo: migrate this
  const legacyBounceRate = !results?.some(({ results = {} }) =>
    Object.values(results).some((e) => e.createdAt),
  );

  let paddedResults: SurveyEntry[] = (results || []).map((result) => {
    const intro = {
      [pages[0]?.uid]: {
        timeStamp: result.createdAt,
      } as QuestionEntry<PageType>,
    };

    const firstPage = {
      [pages[1]?.uid]: {
        createdAt: result.createdAt,
      } as QuestionEntry<PageType>,
    };

    const outro = {
      [pages[pages.length - 1]?.uid]: {
        timeStamp: result.createdAt,
      } as QuestionEntry<PageType>,
    };

    const paddedValues = {
      ...intro,
      // in case no entries were submitted, we need to set the first page as viewed
      ...firstPage,
      ...result.results,
      // in case the survey was completed, we need to set the outro
      // so that the last page is not counted as bounced
      ...(result.completed ? outro : {}),
    };

    return { ...result, results: paddedValues };
  });

  if (legacyBounceRate) {
    paddedResults = paddedResults.map((result) => {
      const entries = Object.entries(result.results);
      const [lastEntryId, lastEntryValue] =
        entries.filter(([, value]) => !!value.timeStamp).pop() || [];
      const currentPageIndex = pages.findIndex((e) => e.uid === lastEntryId);
      const nextPage = pages[currentPageIndex + 1];

      if (!nextPage) return result;

      const answeredQuestions = Object.fromEntries(
        entries.map(([uid, value]) => [
          uid,
          {
            ...value,
            ...(nextPage && value.timeStamp
              ? { createdAt: value.timeStamp }
              : {}),
          },
        ]),
      );

      return {
        ...result,
        results: {
          ...answeredQuestions,
          [nextPage.uid]: {
            value: `legacy bounced page ${nextPage.title || nextPage.type}`,
            createdAt: new Date(
              lastEntryValue?.timeStamp ?? new Date().getTime() + 100,
            ).toISOString(),
          },
        },
      };
    });
  }

  const totalsRaw = {} as Record<string, number[]>;

  const raw =
    paddedResults.reduce(
      (acc, result) => {
        const entries = Object.entries(result.results);
        entries
          .sort(
            ([, a], [, b]) =>
              new Date(a?.createdAt || a?.viewed || "").getTime() -
              new Date(b?.createdAt || b?.viewed || "").getTime(),
          )
          .forEach(([uid, value], i, arr) => {
            if (
              pagesById[uid]?.type === "intro" ||
              pagesById[uid]?.type === "outro"
            )
              return;
            if (!acc[uid])
              acc[uid] = {
                bounced: 0,
                ms: [],
              };

            if (i === arr.length - 1) {
              acc[uid].bounced = (acc[uid].bounced || 0) + 1;
            }
            if (!acc[uid].ms) acc[uid].ms = [];
            const [, nextValue] = arr[i + 1] || [];
            const currentStart = value.createdAt;
            const currentEnd = nextValue?.createdAt || result.completed;

            const ms = Math.max(
              0,
              new Date(currentEnd || 0).getTime() -
                new Date(currentStart || 0).getTime(),
            );

            if (ms < 300000 && value.createdAt && currentEnd) {
              acc[uid].ms.push(ms);
              if (!totalsRaw[result.id]) totalsRaw[result.id] = [];
              totalsRaw[result.id].push(ms);
            }
          });
        return acc;
      },
      {} as Record<string, { bounced: number; ms: number[] }>,
    ) || {};

  const totals = Object.values(totalsRaw)
    /**
     * do not filter those out because we also want to see the average completion time if no one completed the survey
     */
    //.filter((e) => e.length > (pages.length - 3))
    .map((e) => e.reduce((acc, curr) => acc + curr, 0))
    .sort((a, b) => a - b);

  const stats = Object.fromEntries(
    Object.entries(raw).map(([pageUid, value]) => {
      const answered = answeredQuestion(pagesById[pageUid] || {});
      const ms = value.ms.sort((a, b) => a - b);
      const total = ms.reduce((acc, curr) => acc + curr, 0);
      return [
        pageUid,
        {
          ...value,
          ms,
          total,
          bounceRate: value.bounced / results.length,
          median: ms[Math.floor(ms.length / 2)],
          average: total / (answered.length || 1),
        },
      ];
    }),
  );

  const getPageStats = (index: number) => {
    const page = pages[index];
    const { bounced, median, average } = stats[page.uid] || {};

    const answeredQuestions = answeredQuestion(page || {});
    const answeredQuestionsRate = `${
      answeredQuestions.length || 0
    } / ${results?.length || 0}`;

    const bounceRate = new Intl.NumberFormat(i18n.resolvedLanguage, {
      style: "percent",
      maximumFractionDigits: 1,
    }).format((bounced || 0) / (results?.length || 1));
    return {
      bounced,
      median,
      average,
      bounceRate,
      answeredQuestions,
      answeredQuestionsRate,
      pageType: page?.type,
    };
  };
  return { stats, getPageStats, totals };
};

/**
 * Shared logic for choice-based components (MultipleChoice, ImageChoice)
 * Handles common state management, sorting, and "other" option logic
 */
export const useChoiceLogic = <
  T extends "choice" | "choiceImage" | "budgetAllocation",
>(
  page: Page<T>,
  mode: "edit" | "display",
  defaults: ReturnType<typeof useDefaultValues>,
) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  // Input type determination
  const inputType =
    page.type === "budgetAllocation" ||
    page.settings?.multipleChoice?.enabled !== true
      ? ("radio" as const)
      : ("checkbox" as const);

  // Sorted options with random sorting support
  const sortedOptions = useRandomSorting(page, "columns", mode);

  // Other option configuration
  const otherOption = useMemo<InputRow<T>>(() => {
    const baseLabel =
      page.type !== "budgetAllocation" && getTextResponseSetting(page)?.label;
    return {
      uid: "other",
      label:
        ((baseLabel && translation?.[baseLabel]) || baseLabel) ??
        defaults.defaultOtherLabel,
    } as InputRow<T>;
  }, [
    page.type,
    getTextResponseSetting(page)?.label,
    defaults.defaultOtherLabel,
    translation,
    t,
  ]);

  // Final columns (including other option in display mode)
  const columns = useMemo(() => {
    return mode === "display" &&
      page.type !== "budgetAllocation" &&
      getTextResponseSetting(page)?.enabled
      ? [...sortedOptions, otherOption]
      : sortedOptions;
  }, [
    sortedOptions,
    otherOption,
    mode,
    page.type,
    getTextResponseSetting(page)?.enabled,
  ]);

  // Other option text configuration
  const textResponseConfig = useMemo(() => {
    if (
      page.type === "budgetAllocation" ||
      !getTextResponseSetting(page)?.enabled
    )
      return null;

    return {
      placeholder:
        getTextResponseSetting(page)?.placeholder ??
        defaults.placeholderTextResponse,
      required: getTextResponseSetting(page)?.required,
      maxChars: getTextResponseSetting(page)?.maxChars,
      label: getTextResponseSetting(page)?.label,
    };
  }, [page, defaults]);

  return {
    inputType,
    sortedOptions,
    otherOption,
    columns,
    textResponseConfig,
  };
};

/**
 * Shared drag and drop logic for choice-based components
 * Handles drag state and provides drag handlers for both MultipleChoice and ImageChoice
 */
export const useChoiceDragDrop = <
  T extends "choice" | "choiceImage" | "budgetAllocation",
>(
  columns: InputColumn<T>[],
  editQuestion: ReturnType<typeof useEditQuestion>,
) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Common drag end handler for hello-pangea/dnd (MultipleChoice)
  const onDragEndPangea = useOnDragEnd(columns, (columns: InputColumn<T>[]) => {
    editQuestion.updateQuestion(
      (e) => ({
        ...e,
        columns,
      }),
      "options",
    );
  });

  // Common drag end handler for dnd-kit (ImageChoice)
  const handleDragEnd = (event: {
    active: { id: string };
    over?: { id: string } | null;
  }) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = columns.findIndex((item) => item.uid === active.id);
    const newIndex = columns.findIndex((item) => item.uid === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newColumns = [...columns];
      const [removed] = newColumns.splice(oldIndex, 1);
      newColumns.splice(newIndex, 0, removed);

      editQuestion.updateQuestion(
        (e) => ({
          ...e,
          columns: newColumns,
        }),
        "options",
      );
    }
  };

  const handleDragStart = ({ active }: { active: { id: string } }) => {
    setActiveId(active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return {
    activeId,
    onDragEndPangea,
    handleDragEnd,
    handleDragStart,
    handleDragCancel,
  };
};

export const useToPoints = () => {
  const { t, i18n } = useTranslation();
  const formatNumber = new Intl.NumberFormat(i18n.resolvedLanguage, {
    useGrouping: true,
  });
  const toPoints = (val: number) =>
    `${formatNumber.format(val)} ${
      val === 1 ? t("global:point") : t("global:points")
    }`;
  return { toPoints };
};
export const useCurrency = (locale: string) => {
  const { i18n } = useTranslation();
  const toCurrency = (
    value: number,
    options = {} as Intl.NumberFormatOptions,
  ) => {
    const currencyOptions: Intl.NumberFormatOptions = {
      style: "currency",
      currency: "EUR",
      useGrouping: false,
      ...options,
    };
    const formattedValue =
      value?.toLocaleString(i18n.resolvedLanguage, {
        maximumFractionDigits: 2,
        ...currencyOptions,
      }) || "0";

    /**
     * this little transformation is needed to get the correct
     * number of fraction digits with the compact notation
     * e.g. 12300 is 12.3k in english, but 12.300 in german
     * so we need 1 fraction digit in english and 0 in german
     * another example would be 1234567 which is 1.23m in english
     * and 1,23 Mio. in german, so we need 2 fraction digits for both
     */
    const float = Number.parseFloat(
      formattedValue.replace("€", "").replace(",", "."),
    );
    const fraction = String(float).split(".")[1]?.length || 0;

    if (options?.notation === "compact" && fraction > 0) {
      currencyOptions.maximumFractionDigits = fraction;
    }

    return value?.toLocaleString(locale, currencyOptions);
  };

  return { toCurrency };
};
